import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Building2, Check, ExternalLink, Mail, MapPin, Pencil, Phone, UserRound, Video, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatMinutes, formatShortTime, relativeTime } from '../lib/format';
import {
  EARLY_START_MINUTES,
  MEETING_STATUS_FLOW,
  dayKey,
  meetingMinutes,
  type MeetingAnswer,
  type MeetingDetail,
  type MeetingParticipant,
  type MeetingStatus,
} from '../lib/meetings';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Detail,
  ErrorState,
  LoadingBlock,
  PageHeader,
  statusTone,
  useToast,
  type BadgeTone,
} from '../components/ui';
import { meetingDay, meetingRefusal, meetingTimes } from './Meetings';

const ANSWER_TONE: Record<MeetingAnswer, BadgeTone> = { ACCEPTED: 'success', DECLINED: 'error', PENDING: 'warning' };
const PLACE_ICON = { OFFICE: Building2, OUTSIDE: MapPin, ONLINE: Video } as const;
const LINK = /^https?:\/\//i;

/**
 * Route: `/meetings/:id` — one meeting: where it stands, when and where, who
 * called it and who it is for, its agenda, and everyone's answer. Whoever is asked
 * accepts or declines it here; whoever called it changes or cancels it until it
 * is started, and moves its status; an administrator cancels it and moves it too.
 * Its notifications open here — the phone's on the web.
 */
export function MeetingDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: meeting, isLoading, error, refetch } = useQuery({
    queryKey: ['meetings', 'one', id],
    queryFn: () => api.get<MeetingDetail>(`/meetings/${id}`),
  });

  const accept = useMutation({
    mutationFn: () => api.post(`/meetings/${id}/accept`, {}),
    onSuccess: () => {
      toast.success(t('meetings.acceptedToast'));
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
    onError: (e: Error) => toast.error(meetingRefusal(e, t)),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !meeting) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const { can } = meeting;
  const over = new Date(meeting.endsAt).getTime() <= Date.now();
  const underWay = !over && new Date(meeting.startsAt).getTime() <= Date.now();
  const cancelled = meeting.status === 'CANCELLED';
  const PlaceIcon = PLACE_ICON[meeting.place];
  // Back to the calendar on the meeting's own day.
  const back = `/meetings?date=${dayKey(new Date(meeting.startsAt))}&day=${dayKey(new Date(meeting.startsAt))}`;

  const groups: Array<{ answer: MeetingAnswer; title: TranslationKey; people: MeetingParticipant[] }> = [
    { answer: 'ACCEPTED', title: 'meetings.answer.ACCEPTED', people: meeting.participants.filter((p) => p.answer === 'ACCEPTED') },
    { answer: 'PENDING', title: 'meetings.answer.PENDING', people: meeting.participants.filter((p) => p.answer === 'PENDING') },
    { answer: 'DECLINED', title: 'meetings.answer.DECLINED', people: meeting.participants.filter((p) => p.answer === 'DECLINED') },
  ];

  return (
    <>
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('meetings.title')}
        </Link>
      </div>

      <PageHeader
        title={<bdi className={clsx(cancelled && 'line-through decoration-2')}>{meeting.title}</bdi>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {over && !cancelled && meeting.status !== 'DONE' ? (
              // Over a moment ago: the clock closes it as done within half a minute.
              <Badge tone="neutral">{t('meetings.status.OVER')}</Badge>
            ) : (
              <Badge tone={statusTone(meeting.status)} dot={meeting.status === 'IN_PROGRESS'}>
                {t(`meetings.status.${meeting.status}` as TranslationKey)}
              </Badge>
            )}
            {underWay && meeting.status === 'SCHEDULED' && (
              <Badge tone="info" dot>
                {t('meetings.status.NOW')}
              </Badge>
            )}
            {meeting.role === 'ORGANIZER' && <Badge tone="brand">{t('meetings.look.organizer')}</Badge>}
            {meeting.myAnswer && (
              <Badge tone={ANSWER_TONE[meeting.myAnswer]}>{t(`meetings.mine.${meeting.myAnswer}` as TranslationKey)}</Badge>
            )}
            <span className="tabular">
              {meetingDay(meeting.startsAt, locale)} · {meetingTimes(meeting, locale)}
            </span>
          </span>
        }
        action={
          (can.answer || can.edit || can.cancel) && (
            <>
              {can.answer && meeting.myAnswer !== 'ACCEPTED' && (
                <Button
                  variant="primary"
                  icon={<Check className="h-4 w-4" />}
                  loading={accept.isPending}
                  onClick={() => accept.mutate()}
                >
                  {t('meetings.accept')}
                </Button>
              )}
              {can.answer && meeting.myAnswer !== 'DECLINED' && (
                <Button icon={<X className="h-4 w-4" />} onClick={() => navigate(`/meetings/${id}/decline`)}>
                  {t('meetings.decline')}
                </Button>
              )}
              {can.edit && (
                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/meetings/${id}/edit`)}>
                  {t('meetings.edit')}
                </Button>
              )}
              {can.cancel && (
                <Button variant="danger" onClick={() => navigate(`/meetings/${id}/cancel`)}>
                  {t('meetings.cancel')}
                </Button>
              )}
            </>
          )
        }
      />

      {cancelled ? (
        <div className="mb-5 rounded-md border border-error/40 bg-error-bg px-4 py-2.5 text-body-sm text-error-fg dark:bg-error/12 dark:text-error-dark">
          {meeting.guest?.cancelledAt
            ? t('booking.guestCancelled', { when: formatDateTime(meeting.cancelledAt, locale) })
            : t('meetings.cancelledBanner', {
                name: meeting.cancelledBy ? localized(meeting.cancelledBy) : '—',
                when: formatDateTime(meeting.cancelledAt, locale),
              })}
          {meeting.cancelNote && (
            <span className="mt-1 block whitespace-pre-wrap" dir="auto">
              “{meeting.cancelNote}”
            </span>
          )}
        </div>
      ) : (
        can.answer && (
          <div
            className={clsx(
              'mb-5 rounded-md border px-4 py-2.5 text-body-sm text-[var(--text-primary)]',
              meeting.myAnswer === 'PENDING' ? 'border-warning/40 bg-warning/8' : 'border-primary/30 bg-primary/8',
            )}
          >
            {t(`meetings.yourAnswer.${meeting.myAnswer ?? 'PENDING'}` as TranslationKey)}
            {meeting.myNote && (
              <span className="mt-1 block text-muted" dir="auto">
                {t('meetings.yourNote')}: “{meeting.myNote}”
              </span>
            )}
          </div>
        )
      )}

      {/* A guest picked its time (2026-11-05): it is cancelled, not moved. */}
      {meeting.guest && !cancelled && meeting.role === 'ORGANIZER' && meeting.status === 'SCHEDULED' && !over && (
        <p className="mb-5 rounded-md border border-info/30 bg-info/8 px-4 py-2.5 text-body-sm text-[var(--text-primary)]">
          {t('booking.guestLocked')}
        </p>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          {!cancelled && <MeetingStatusCard meeting={meeting} />}

          <Card>
            <CardHeader title={t('meetings.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('meetings.date')}>
                <span className="tabular">{meetingDay(meeting.startsAt, locale)}</span>
              </Detail>
              <Detail label={t('meetings.time')}>
                <span className="tabular">
                  {meetingTimes(meeting, locale)} · {formatMinutes(meetingMinutes(meeting), locale)}
                </span>
              </Detail>
              <Detail label={t('meetings.where')}>
                <span className="inline-flex flex-col items-end gap-0.5">
                  <span className="inline-flex items-center gap-1.5">
                    <PlaceIcon className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
                    {t(`meetings.place.${meeting.place}` as TranslationKey)}
                  </span>
                  {meeting.location &&
                    (meeting.place === 'ONLINE' && LINK.test(meeting.location) ? (
                      <a
                        href={meeting.location}
                        target="_blank"
                        rel="noopener noreferrer"
                        dir="ltr"
                        className="inline-flex max-w-full items-center gap-1 break-all text-primary-600 hover:underline dark:text-primary-400"
                      >
                        {meeting.location}
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      </a>
                    ) : (
                      <span className="whitespace-pre-wrap text-muted" dir="auto">
                        {meeting.location}
                      </span>
                    ))}
                </span>
              </Detail>
              <Detail label={t('meetings.calledBy')}>
                <span className="inline-flex flex-col items-end">
                  <span>
                    {localized(meeting.organizer)}
                    {meeting.organizerId === user?.id && <span className="text-muted"> ({t('meetings.you')})</span>}
                  </span>
                  {meeting.organizer.jobTitle && <span className="text-caption text-muted">{meeting.organizer.jobTitle}</span>}
                </span>
              </Detail>
              <Detail label={t('meetings.forLabel')}>
                <span className="inline-flex flex-col items-end">
                  <span>{t(`meetings.audience.${meeting.audience}` as TranslationKey)}</span>
                  {meeting.departments.length > 0 && (
                    <span className="text-caption text-muted">
                      <bdi>{meeting.departments.map((d) => localized(d)).join(locale === 'ar' ? '، ' : ', ')}</bdi>
                    </span>
                  )}
                </span>
              </Detail>
              <Detail label={t('meetings.reminder')}>
                {meeting.reminderMinutes > 0
                  ? t('meetings.reminderBefore', { time: formatMinutes(meeting.reminderMinutes, locale) })
                  : t('meetings.reminderOff')}
              </Detail>
            </dl>
          </Card>

          <Card>
            <CardHeader title={t('meetings.agenda')} />
            {meeting.agenda ? (
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {meeting.agenda}
              </p>
            ) : (
              <p className="text-body-sm text-muted">{t('meetings.noAgenda')}</p>
            )}
          </Card>
        </div>

        <div className="min-w-0 space-y-5">
          {meeting.guest && <GuestCard meeting={meeting} />}
          {meeting.audience !== 'GUEST' && (
          <Card>
            <CardHeader
              title={t('meetings.answers')}
              subtitle={t('meetings.answersShort', { accepted: meeting.answers.accepted, total: meeting.answers.total })}
            />
            <div className="mb-4 grid grid-cols-3 gap-2 text-center">
              {groups.map((g) => (
                <div key={g.answer} className="rounded-md bg-[var(--surface-sunken)] px-2 py-2">
                  <span className="block text-h3 tabular text-[var(--text-primary)]">{g.people.length}</span>
                  <span className="block text-caption text-muted">{t(g.title)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {groups
                .filter((g) => g.people.length > 0)
                .map((g) => (
                  <section key={g.answer}>
                    <h4 className="mb-1.5 text-overline uppercase text-[var(--text-tertiary)]">{t(g.title)}</h4>
                    <ul className="space-y-1.5">
                      {g.people.map((p) => (
                        <li key={p.user.id} className="flex items-start justify-between gap-3 text-body-sm">
                          <span className="min-w-0">
                            <span className="block truncate text-[var(--text-primary)]">
                              <bdi>{localized(p.user)}</bdi>
                              {p.user.id === user?.id && <span className="text-muted"> ({t('meetings.you')})</span>}
                            </span>
                            {p.user.jobTitle && <span className="block truncate text-caption text-faint">{p.user.jobTitle}</span>}
                            {p.note && (
                              <span className="mt-0.5 block whitespace-pre-wrap text-caption text-muted" dir="auto">
                                “{p.note}”
                              </span>
                            )}
                          </span>
                          {p.answeredAt && p.answer !== 'PENDING' && (
                            <span className="shrink-0 text-caption text-faint" title={formatDateTime(p.answeredAt, locale)}>
                              {relativeTime(p.answeredAt, locale)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
            </div>
          </Card>
          )}
        </div>
      </div>
    </>
  );
}

/** Who booked it through a booking link (2026-11-05): their name, how to reach them, and what they wrote. */
function GuestCard({ meeting }: { meeting: MeetingDetail }) {
  const { t, locale } = useI18n();
  const guest = meeting.guest!;
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <UserRound className="h-5 w-5 text-[var(--text-tertiary)]" aria-hidden />
            {t('booking.guest')}
          </span>
        }
        subtitle={t('booking.guestBooked', { when: formatDateTime(guest.createdAt, locale) })}
      />
      <div className="space-y-2 text-body-sm">
        <p className="font-medium text-[var(--text-primary)]">
          <bdi>{guest.name}</bdi>
          {guest.company && <span className="block text-caption font-normal text-muted"><bdi>{guest.company}</bdi></span>}
        </p>
        <a
          href={`mailto:${guest.email}`}
          dir="ltr"
          className="flex items-center gap-1.5 break-all text-primary-600 hover:underline dark:text-primary-400"
        >
          <Mail className="h-4 w-4 shrink-0" aria-hidden />
          {guest.email}
        </a>
        {guest.phone && (
          <a href={`tel:${guest.phone.replace(/[^+0-9]/g, '')}`} dir="ltr" className="flex items-center gap-1.5 text-[var(--text-primary)] hover:underline">
            <Phone className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            {guest.phone}
          </a>
        )}
        <p className="text-caption text-faint" dir="ltr">
          {guest.timezone}
        </p>
      </div>
    </Card>
  );
}

/**
 * Where it stands (operator, 2026-10-26: "add status to meeting scheduled -
 * started - in progress - done"): the four steps, since when and by whom, and —
 * for whoever called it, or an administrator — the moves open now. Each move is
 * asked first: it never goes back.
 */
function MeetingStatusCard({ meeting }: { meeting: MeetingDetail }) {
  const { t, locale, localized } = useI18n();
  const { can: allowed } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<MeetingStatus | null>(null);

  const move = useMutation({
    mutationFn: (status: MeetingStatus) => api.post<MeetingDetail>(`/meetings/${meeting.id}/status`, { status }),
    onSuccess: (_saved, status) => {
      setAsked(null);
      toast.success(t('meetings.statusToast', { status: t(`meetings.status.${status}` as TranslationKey) }));
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
    onError: (e: Error) => {
      setAsked(null);
      toast.error(meetingRefusal(e, t));
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  const at = MEETING_STATUS_FLOW.indexOf(meeting.status);
  const runs = meeting.role === 'ORGANIZER' || allowed(P.MEETINGS_MANAGE);
  const over = new Date(meeting.endsAt).getTime() <= Date.now();
  const moves = meeting.can.status;
  const statusName = (status: MeetingStatus) => t(`meetings.status.${status}` as TranslationKey);

  let note: string;
  if (meeting.status === 'SCHEDULED') {
    const from = new Date(new Date(meeting.startsAt).getTime() - EARLY_START_MINUTES * 60_000);
    note =
      runs && !over && moves.length === 0
        ? t('meetings.statusStartsFrom', { time: formatShortTime(from.toISOString(), locale) })
        : t(runs && moves.length > 0 ? 'meetings.statusHintRun' : 'meetings.statusNotStarted');
  } else {
    const when = formatDateTime(meeting.statusAt, locale);
    note = meeting.statusBy
      ? t('meetings.statusSinceBy', { when, name: localized(meeting.statusBy) })
      : t(meeting.status === 'DONE' ? 'meetings.statusClosedAtEnd' : 'meetings.statusSince', { when });
  }

  return (
    <Card>
      <CardHeader title={t('meetings.statusTitle')} />
      <ol className="flex items-start" aria-label={t('meetings.statusTitle')}>
        {MEETING_STATUS_FLOW.map((status, i) => {
          const reached = i <= at;
          const current = i === at;
          return (
            <li
              key={status}
              className="relative flex min-w-0 flex-1 flex-col items-center gap-1.5 text-center"
              aria-current={current ? 'step' : undefined}
            >
              {/* The line from the step before, to this one's middle. */}
              {i > 0 && (
                <span
                  aria-hidden
                  className={clsx('absolute end-1/2 top-3 h-0.5 w-full -translate-y-1/2', reached ? 'bg-primary' : 'bg-[var(--line)]')}
                />
              )}
              <span
                className={clsx(
                  'relative grid h-6 w-6 place-items-center rounded-full',
                  reached ? 'bg-primary text-white' : 'border-2 hairline bg-[var(--surface-card)]',
                  current && meeting.status !== 'DONE' && 'ring-4 ring-primary/15',
                )}
                aria-hidden
              >
                {reached && (!current || meeting.status === 'DONE') ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                ) : current ? (
                  <span className={clsx('h-2 w-2 rounded-full bg-white', meeting.status === 'IN_PROGRESS' && 'animate-pulse')} />
                ) : null}
              </span>
              <span
                className={clsx(
                  'max-w-full px-0.5 text-caption leading-tight',
                  current ? 'font-semibold text-[var(--text-primary)]' : reached ? 'text-[var(--text-secondary)]' : 'text-faint',
                )}
              >
                {statusName(status)}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t hairline pt-3">
        <p className="min-w-0 flex-1 basis-56 text-body-sm text-muted">{note}</p>
        {moves.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {moves.map((status, i) => (
              <Button key={status} size="sm" variant={i === 0 ? 'primary' : 'secondary'} onClick={() => setAsked(status)}>
                {t(`meetings.move.${status}` as TranslationKey)}
              </Button>
            ))}
          </div>
        )}
      </div>

      {asked && (
        <ConfirmDialog
          title={t('meetings.moveTitle', { status: statusName(asked) })}
          confirmLabel={t(`meetings.move.${asked}` as TranslationKey)}
          busy={move.isPending}
          onConfirm={() => move.mutate(asked)}
          onCancel={() => setAsked(null)}
        >
          <p>
            {t(
              asked === 'DONE'
                ? 'meetings.moveDoneNote'
                : meeting.status === 'SCHEDULED'
                  ? 'meetings.moveStartNote'
                  : 'meetings.moveForwardNote',
            )}
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}
